/* global Swiper */
function loadSwiper(src) {
  if (typeof Swiper !== 'undefined') return Promise.resolve();
  if (!window.__swiperLoading) {
    window.__swiperLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return window.__swiperLoading;
}

if (!customElements.get('feature-product')) {
  customElements.define(
    'feature-product',
    class FeatureProduct extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-fp-variants]');
        this.variants = json ? JSON.parse(json.textContent) : {};

        this.variantIdInput = this.querySelector('[data-fp-variant-id]');
        this.priceEl = this.querySelector('[data-fp-price]');
        this.compareWrap = this.querySelector('[data-fp-compare-wrapper]');
        this.compareEl = this.querySelector('[data-fp-compare]');
        this.offEl = this.querySelector('[data-fp-off]');
        this.packLabel = this.querySelector('[data-fp-pack-label]');
        this.submitBtn = this.querySelector('[data-fp-submit]');
        this.buyTextEl = this.querySelector('[data-fp-buy-text]');
        this.buyText =
          this.submitBtn?.dataset.buyText || (this.buyTextEl ? this.buyTextEl.textContent.trim() : 'Buy Now');
        this.soldText = this.submitBtn ? this.submitBtn.dataset.soldText || 'Sold out' : 'Sold out';
        this.unavailableText = this.submitBtn?.dataset.unavailableText || this.soldText;

        this.onPackChange = this.onPackChange.bind(this);
        this.packInputs = this.querySelectorAll('[data-fp-pack]');
        this.packInputs.forEach((input) => input.addEventListener('change', this.onPackChange));

        this.onOptionChange = this.onOptionChange.bind(this);
        this.optionSelects = this.querySelectorAll('[data-mpf-option]');
        this.optionSelects.forEach((select) => select.addEventListener('change', this.onOptionChange));

        this.descEl = this.querySelector('[data-fp-desc]');
        this.readMoreBtn = this.querySelector('[data-fp-read-more]');
        if (this.descEl && this.readMoreBtn) {
          this.onReadMore = this.onReadMore.bind(this);
          this.onDescTransitionEnd = this.onDescTransitionEnd.bind(this);
          this.syncReadMore = this.syncReadMore.bind(this);
          this.readMoreBtn.addEventListener('click', this.onReadMore);
          this.descEl.addEventListener('transitionend', this.onDescTransitionEnd);
          this.syncReadMore();
          // a webfont swap re-flows the text, so a 3-line fit can become 4 after load
          document.fonts?.ready.then(this.syncReadMore);
          // and a width change (rotate, resize, breakpoint) changes the line count too
          this.descObserver = new ResizeObserver(this.syncReadMore);
          this.descObserver.observe(this.descEl);
        }

        this.querySelectorAll('[data-mpf-accordion]').forEach((btn) =>
          btn.addEventListener('click', () => this.toggleAccordion(btn)),
        );

        this.sticky = this.querySelector('[data-mpf-sticky]');
        if (this.sticky && this.submitBtn) {
          this.stickyBtn = this.sticky.querySelector('[data-mpf-sticky-submit]');
          this.stickyText = this.sticky.querySelector('[data-mpf-sticky-text]');
          this.stickyPrice = this.sticky.querySelector('[data-mpf-sticky-price]');
          this.stickyCompare = this.sticky.querySelector('[data-mpf-sticky-compare]');
          this.stickyVariant = this.sticky.querySelector('[data-mpf-sticky-variant]');
          this.stickyImg = this.sticky.querySelector('.main-product-feature__sticky-img');
          // product-form.js only animates the main button (the sticky one lives outside <product-form>) — mirror it,
          // so an add from the bar shows the spinner too and it clears on every outcome (success, 422, network error)
          if (this.stickyBtn) {
            this.stickySpinner = this.stickyBtn.querySelector('.loading__spinner');
            this.loadingObserver = new MutationObserver(() => this.syncStickyLoading());
            this.loadingObserver.observe(this.submitBtn, {
              attributes: true,
              attributeFilter: ['class', 'aria-disabled'],
            });
          }
          // portal to <body>: a transform/z-index on any section ancestor would otherwise trap the bar's z-index
          document.body.append(this.sticky);
          // the bar is on from first paint (Liquid ships `is-visible`); this only adds the body padding that
          // reserves room for it, since no scroll event gates it any more
          this.setStickyVisible(true);

          // an add from the sticky bar that fails shows its error by the main button — bring it in when it's off-screen
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, () => {
              const rect = this.submitBtn.getBoundingClientRect();
              const offScreen = rect.bottom <= 0 || rect.top >= window.innerHeight;
              if (offScreen) this.submitBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
            });
          }
        }

        this.freqLabel = this.querySelector('[data-fp-freq-label]');
        this.onFreqChange = this.onFreqChange.bind(this);
        this.freqInputs = this.querySelectorAll('[data-fp-freq]');
        this.freqInputs.forEach((input) => input.addEventListener('change', this.onFreqChange));

        if (this.dataset.swiperSrc) {
          loadSwiper(this.dataset.swiperSrc)
            .then(() => {
              if (this.isConnected) this.initGallery();
            })
            .catch(() => {
              /* gallery stays as a static, scrollable stack if Swiper fails */
            });
        }
      }

      disconnectedCallback() {
        this.packInputs?.forEach((input) => input.removeEventListener('change', this.onPackChange));
        this.optionSelects?.forEach((select) => select.removeEventListener('change', this.onOptionChange));
        this.packsAbort?.abort();
        this.freqInputs?.forEach((input) => input.removeEventListener('change', this.onFreqChange));
        this.readMoreBtn?.removeEventListener('click', this.onReadMore);
        this.descEl?.removeEventListener('transitionend', this.onDescTransitionEnd);
        this.descObserver?.disconnect();
        this.loadingObserver?.disconnect();
        this.unsubscribeCartError?.();
        document.body.classList.remove('mpf-sticky-active');
        // take the portalled bar back, so a section removed in the editor doesn't leave it orphaned in <body>
        if (this.sticky) {
          this.sticky.classList.remove('is-visible');
          this.append(this.sticky);
        }
        this.mainSwiper?.destroy(true, true);
        this.thumbsSwiper?.destroy(true, true);
      }

      initGallery() {
        const mainEl = this.querySelector('[data-fp-main-swiper]');
        if (!mainEl || typeof Swiper === 'undefined') return;

        const thumbsEl = this.querySelector('[data-fp-thumbs-swiper]');
        if (thumbsEl) {
          this.thumbsSwiper = new Swiper(thumbsEl, {
            slidesPerView: 'auto',
            spaceBetween: 8,
            slidesOffsetAfter: 8,
            freeMode: true,
            mousewheel: { forceToAxis: true },
            watchSlidesProgress: true,
            breakpoints: { 1025: { direction: 'vertical' } },
          });
        }

        this.mainSwiper = new Swiper(mainEl, {
          slidesPerView: 1,
          autoHeight: true,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          speed: 700,
          pagination: { el: this.querySelector('[data-fp-pagination]'), clickable: true },
          navigation: {
            nextEl: this.querySelector('[data-fp-next]'),
            prevEl: this.querySelector('[data-fp-prev]'),
          },
          thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
        });

        // Deliberately no jump to the checked pack's image here: the gallery always
        // opens on the first media slide. Variant-driven image switching still runs,
        // it just starts from a change event (onPackChange) rather than page load.
      }

      showVariantImage(imageId, speed) {
        if (!imageId || !this.mainSwiper) return;
        const slides = this.querySelectorAll('[data-fp-image-id]');
        const index = Array.from(slides).findIndex((slide) => slide.dataset.fpImageId === String(imageId));
        // -1 = the variant's image isn't in the gallery; leave the current slide alone
        if (index < 0 || index === this.mainSwiper.activeIndex) return;
        this.mainSwiper.slideTo(index, speed);
      }

      // The button only earns its place when the text actually overflows the CSS clamp.
      syncReadMore() {
        // while open the clamp is overridden inline, so there is nothing to compare against
        if (this.readMoreBtn.getAttribute('aria-expanded') === 'true') return;
        const clamp = parseFloat(getComputedStyle(this.descEl).maxHeight);
        if (!clamp) return;
        // 1px of slack: fractional line heights round scrollHeight up on an exact fit
        this.readMoreBtn.hidden = this.descEl.scrollHeight <= clamp + 1;
      }

      // clamp px <-> full px. Animating to/from `none` doesn't transition at all,
      // so opening has to run through an explicit pixel value.
      onReadMore() {
        const expanded = this.readMoreBtn.getAttribute('aria-expanded') === 'true';
        const full = `${this.descEl.scrollHeight}px`;

        if (expanded) {
          // max-height is `none` by now — pin it to px, flush it, then clear the inline value
          // so it animates back down to the clamp. Without the read-back both writes coalesce
          // and it snaps shut instead of transitioning.
          this.descEl.style.maxHeight = full;
          void this.descEl.offsetHeight;
          this.descEl.style.maxHeight = '';
        } else {
          this.descEl.style.maxHeight = full;
        }

        this.readMoreBtn.setAttribute('aria-expanded', String(!expanded));
        this.readMoreBtn.textContent = expanded ? 'Read More' : 'Read Less';
      }

      onDescTransitionEnd(event) {
        if (event.target !== this.descEl || event.propertyName !== 'max-height') return;
        // release the cap once open, so a resize or font swap can't clip the text at a stale height
        if (this.readMoreBtn.getAttribute('aria-expanded') === 'true') this.descEl.style.maxHeight = 'none';
      }

      // called once, on connect — the bar is permanent now, so nothing toggles it back off
      setStickyVisible(visible) {
        this.sticky.classList.toggle('is-visible', visible);
        // body padding keeps the footer's last row from sitting under the bar
        document.body.classList.toggle('mpf-sticky-active', visible);
      }

      syncStickyLoading() {
        const loading = this.submitBtn.classList.contains('loading');
        this.stickyBtn.classList.toggle('loading', loading);
        this.stickySpinner?.classList.toggle('hidden', !loading);
        const ariaDisabled = this.submitBtn.getAttribute('aria-disabled');
        if (ariaDisabled) {
          this.stickyBtn.setAttribute('aria-disabled', ariaDisabled);
        } else {
          this.stickyBtn.removeAttribute('aria-disabled');
        }
      }

      updateSticky(data, label) {
        if (!this.sticky) return;
        if (this.stickyPrice) this.stickyPrice.textContent = data.price;
        if (this.stickyCompare) {
          this.stickyCompare.textContent = data.compareAt;
          this.stickyCompare.hidden = !data.hasCompare;
        }
        if (this.stickyVariant && label) this.stickyVariant.textContent = label;
        if (this.stickyImg && data.thumb) {
          this.stickyImg.removeAttribute('srcset');
          this.stickyImg.src = data.thumb;
        }
        if (this.stickyBtn) {
          this.stickyBtn.disabled = !data.available;
          if (this.stickyText) {
            this.stickyText.textContent = data.available ? `${this.buyText} · ${data.price}` : this.soldText;
          }
        }
      }

      toggleAccordion(btn) {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        btn.closest('.main-product-feature__accordion')?.classList.toggle('is-open', open);
        // inert keeps links in a collapsed panel out of the tab order while it stays rendered for the animation
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.inert = !open;
      }

      onFreqChange(event) {
        // radios have unique names so every option is a tab stop — uncheck siblings by hand
        this.freqInputs.forEach((input) => (input.checked = input === event.target));
        if (this.freqLabel) this.freqLabel.textContent = event.target.dataset.label || '';
      }

      // variant id for this pack card + the option 2/3 dropdowns ('' when that combination doesn't exist)
      resolveVariantId(packInput) {
        const wanted = [packInput.dataset.label];
        this.optionSelects.forEach((select) => (wanted[select.dataset.mpfOption] = select.value));
        return (
          Object.keys(this.variants).find((key) =>
            this.variants[key].options?.every((value, i) => value === wanted[i]),
          ) || ''
        );
      }

      // re-render the cards after a dropdown change so each one prices its pack in the new combination
      onOptionChange() {
        const checked = this.querySelector('[data-fp-pack]:checked');
        if (!checked) return;
        this.onPackChange({ target: checked });
        if (checked.value) this.refreshPacks(checked.value);
      }

      refreshPacks(variantId) {
        const fieldset = this.querySelector('[data-mpf-packs]');
        if (!fieldset || !this.dataset.sectionId) return;
        // a newer change wins — drop the in-flight render so stale cards can't land last
        this.packsAbort?.abort();
        this.packsAbort = new AbortController();
        fetch(`${window.location.pathname}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
          signal: this.packsAbort.signal,
        })
          .then((response) => response.text())
          .then((html) => {
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mpf-packs]');
            if (!fresh) return;
            this.packInputs.forEach((input) => input.removeEventListener('change', this.onPackChange));
            fieldset.replaceChildren(...fresh.childNodes);
            this.packInputs = fieldset.querySelectorAll('[data-fp-pack]');
            this.packInputs.forEach((input) => input.addEventListener('change', this.onPackChange));
          })
          .catch(() => {
            /* cards keep their old prices; the selected variant and buy button are already correct */
          });
      }

      // no variant exists for this option combination — clear the id so neither button can add a wrong one
      setUnavailable() {
        if (this.variantIdInput) this.variantIdInput.value = '';
        if (this.submitBtn) this.submitBtn.disabled = true;
        if (this.buyTextEl) this.buyTextEl.textContent = this.unavailableText;
        if (this.stickyBtn) this.stickyBtn.disabled = true;
        if (this.stickyText) this.stickyText.textContent = this.unavailableText;
      }

      onPackChange(event) {
        this.packInputs.forEach((input) => (input.checked = input === event.target));
        // with option 2/3 dropdowns the card's own id can be stale — always resolve from every option
        if (this.optionSelects.length) event.target.value = this.resolveVariantId(event.target);
        const data = this.variants[event.target.value];
        if (!data) {
          if (this.optionSelects.length) this.setUnavailable();
          return;
        }

        if (this.variantIdInput) this.variantIdInput.value = event.target.value;
        if (this.packLabel) this.packLabel.textContent = event.target.dataset.label || '';
        if (this.priceEl) this.priceEl.textContent = data.price;

        this.showVariantImage(data.imageId);

        const hasDiscount = data.hasCompare && Number(data.percentOff) > 0;
        if (this.compareWrap) {
          this.compareWrap.hidden = !hasDiscount;
          if (this.compareEl) this.compareEl.textContent = data.compareAt;
          if (this.offEl) this.offEl.textContent = `${data.percentOff}% OFF`;
        }

        // mirror price into every frequency row
        this.querySelectorAll('[data-fp-freq-price]').forEach((el) => {
          el.textContent = data.price;
        });
        this.querySelectorAll('[data-fp-freq-compare]').forEach((el) => {
          el.textContent = data.compareAt;
          el.hidden = !hasDiscount;
        });
        this.querySelectorAll('[data-fp-freq-off]').forEach((el) => {
          el.textContent = `${data.percentOff}%off`;
          el.hidden = !hasDiscount;
        });

        if (this.submitBtn) {
          this.submitBtn.disabled = !data.available;
          if (this.buyTextEl) this.buyTextEl.textContent = data.available ? this.buyText : this.soldText;
        }

        this.updateSticky(data, data.title || event.target.dataset.label);
      }
    },
  );
}
